import React, { useContext, useState, useEffect } from 'react';
import {
  TicketClient,
  GET_FIELD,
  UPDATE_TICKET,
  GET_TECHNICIANS_BY_CRITERIA,
  AccountClient
} from '@nile/graphql';
import { useLazyQuery, useQuery } from '@apollo/client';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';
import {
  toast,
  Loader,
  SearchDropdown,
  getTranslatedText,
  AppContext,
  TICKET_SOURCE,
  Tag,
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  UnknownTicketActions,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import { clone, cloneDeep, isEqual } from 'lodash';
import { Card, Avatar, Spinner, Checkbox } from '@superops/ui-design-system';
import dompurify from 'dompurify';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TicketInfoPopup from '@nile/mangifera/src/Components/Ticket/TicketInfoPopup';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { dompurifier, EventEmitter } from '@nile/components/src/Utils/helpers';
import { getTechnicianVariables } from '@nile/graphql/src/Constants/variablesResolver';

const KanbanCard = styled.div`
  width: ${(props) => (props.isClassicView ? '100%' : 'calc(100% - 20px)')};

  .Superops-Card {
    margin: 0 !important;
    margin-bottom: 16px !important;
    padding: 12px 0;
    background: ${(props) => props.bgColor || 'var(--sops-white-to-grey-bg)'};
    background-color: ${(props) =>
      props.hightLightCard ? 'var(--sops-classic-card-highlight-bg)' : ''};
    box-sizing: border-box;
    border-radius: ${(props) =>
      props.hightLightCard ? '0px 5px 5px 0px !important' : '8px'};
    border-left: ${(props) =>
      props.hightLightCard
        ? '3px solid rgba(9, 30, 245, 0.5) !important'
        : '0px'};
    padding-left: ${(props) => (props.isClassicView ? '5px' : '0px')};
    box-shadow: none !important;
  }

  &:hover .Superops-Card {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ReqClientDetails = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 21px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 10px;
  span {
    margin: 0 5px;
  }
`;

const AiTag = styled.div`
  div {
    font-size: 10px !important;
  }
`;

const ReqClientName = styled.div`
  color: rgba(0, 0, 0, 0.85);
  max-width: ${(props) => props.maxWidth || '110px'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subject = styled.div`
  display: inline-flex;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  cursor: pointer;
`;

const TechnicianContainer = styled.div`
  cursor: pointer;
  position: relative;
  .removeTech {
    position: absolute;
    top: -6px;
    right: -6px;
    display: none;
    background: white;
    border-radius: 50%;
    svg {
      width: 12px;
      height: 12px;
    }
  }
  &:hover .assignTech svg {
    color: #323232;
  }
  &:hover .removeTech {
    display: flex;
    svg:hover {
      color: #ff7a7a;
    }
  }
  .removeTech svg:hover {
    color: #ff7a7a;
  }
`;

const TooltipWrap = styled.div`
  padding: 8px;
  font-size: 12px !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .type {
    margin-bottom: 4px;
  }
`;

const IconTooltipWrap = styled.div`
  font-size: 12px !important;
`;

const FieldInfoWrapper = styled.div`
  min-width: 190px;
  .Polaris-Avatar--sizeSmall {
    width: 20px !important;
    min-width: 20px !important;
    background: #adadad !important;
  }
  .no-info {
    color: #323232;
  }
`;

const Value = styled.div`
  font-size: 12px;
  line-height: 21px;
  margin-left: 8px;
  font-weight: 400;
  color: ${(props) => props?.color || 'rgba(0, 0, 0, 0.8)'};
  white-space: nowrap;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
  &.no-info {
    color: #32323280 !important;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const IdContainer = styled(Header)``;

const StyledTag = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: ${(props) => props.color || '#ffffff'};
  background: ${(props) => props.background};
  border-radius: 4px;
  ${(props) =>
    props.leftAligned &&
    `
    padding: 2px 10px 2px 5px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  `}
  ${(props) =>
    props.rightAligned &&
    `
    padding: 2px 5px 2px 10px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `}
  max-width: ${(props) => props.maxWidth || '120px'};
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  &.status-cell {
    display: inline;
    vertical-align: middle;
  }
`;

const TechnicianListItem = styled.div`
  .Superops-Avatar__Svg {
    width: 25px;
    height: 25px;
  }
  &.selected .Superops-Avatar {
    border: 2px solid #4238fd;
  }
`;

function TicketKanbanCard(props) {
  const {
    context,
    item,
    activeTab,
    board,
    dispatch,
    list,
    intl,
    selectedRecords = [],
    handleCheckboxChange,
    isClassicView,
    hightLightCard,
    eventData,
    setUpdateLaneIds,
    setLiveUpdateTickets,
    isHighlight
  } = props;
  const flags = useFlags();
  const {
    priority,
    displayId,
    subject,
    client,
    requester,
    ticketId,
    status,
    technician,
    source,
    latestReply,
    ticketType,
    site,
    department
  } = item;

  useEffect(() => {
    const payload = eventData?.payload;
    const event = payload?.event;
    if (
      event === 'ADD_TO_LIST' ||
      event === 'REMOVE_FROM_LIST' ||
      event === 'UPDATE'
    ) {
      const isCurrentTicketUpdated =
        payload?.data?.record?.current?.ticketId === ticketId;
      if (isCurrentTicketUpdated) {
        props?.setShowLiveUpdate && props?.setShowLiveUpdate(true);
        const previousData = payload?.data?.record?.previous;
        const updatedProperty = previousData && Object.keys(previousData)?.[0];
        const activeKanbanTab = props?.activeTab;
        setLiveUpdateTickets(ticketId);
        if (updatedProperty === activeKanbanTab) {
          const currentUpdated =
            payload?.data?.record?.current?.[updatedProperty];
          const previousUpdated = previousData?.[updatedProperty];
          setUpdateLaneIds &&
            setUpdateLaneIds([currentUpdated, previousUpdated]);
        } else {
          setUpdateLaneIds && setUpdateLaneIds([list?.id]);
        }
      }
    }
  }, [eventData]);

  const currentProduct = useProduct();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user;
  const isStatus = activeTab === 'status';
  const isTechnician = activeTab === 'technician';
  const isSourceVisible =
    source === TICKET_SOURCE.Schedule || source === TICKET_SOURCE.Ai;
  const [technicianLoading, setTechnicianLoading] = useState(false);

  const { loading, data: priorityValues, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
      }
    }
  });
  const [
    getTechniciansList,
    { loading: techLoading, data: technicians, error: techError }
  ] = useLazyQuery(GET_TECHNICIANS_BY_CRITERIA, {
    client: AccountClient
  });

  const getSourceIcon = () => {
    const entry = Object.entries(TICKET_SOURCE).find(
      ([, value]) => value === source
    );
    return entry ? entry[0] : 'InlineError';
  };

  if (loading) return <Loader />;
  if (error) return <div>An error occurred...</div>;
  const colors = {};
  let currentTechnician = technician ? [technician] : [];
  const fldOptions =
    priorityValues?.getField?.options || priorityValues?.getField?.fieldOptions;
  fldOptions?.forEach((el) => {
    colors[el.value] = el?.details?.colorCode;
  });
  const openTicket = () => {
    if (isClassicView) {
      return;
    }
    context.openTab({
      module: 'TICKET',
      data: {
        subject: subject,
        displayId: displayId,
        ticketId: ticketId
      }
    });
  };

  function updateTechnician(technicianArr = null) {
    setTechnicianLoading(true);
    const technicianDetails = technicianArr && {
      ...technicianArr[technicianArr.length - 1]
    };
    // save technician api call
    if (!isEqual(item.technician, technicianDetails)) {
      TicketClient.mutate({
        mutation: UPDATE_TICKET,
        variables: {
          id: item.ticketId,
          technician: technicianDetails
            ? { userId: technicianDetails.userId }
            : null
        }
      })
        .then((res) => {
          setTechnicianLoading(false);
          if (res?.data?.updateTicket) {
            // toast(
            //   'success',
            //   res?.data?.updateTicket?.technician
            //     ? 'Assign technician success'
            //     : 'Remove technician success'
            // );
            const updatedTicket = res?.data?.updateTicket;
            if (isClassicView) {
              EventEmitter.dispatch('updateClassicCard', updatedTicket);
              EventEmitter.dispatch('updateClassicDetails', updatedTicket);
              return;
            }
            const newBoardData = cloneDeep(board);
            const targetLaneIdx = newBoardData.findIndex(
              (el) => el.id === list.id
            );
            const itemToUpdate = newBoardData[targetLaneIdx].items.findIndex(
              (el) => el.ticketId === updatedTicket.ticketId
            );
            newBoardData[targetLaneIdx].items[itemToUpdate] = {
              ...newBoardData[targetLaneIdx].items[itemToUpdate],
              ...updatedTicket
            };
            dispatch({ type: 'resetBoard', board: newBoardData });
          } else {
            toast('error', 'Something went wrong');
          }
        })
        .catch((err) => {
          setTechnicianLoading(false);
          // toast('error', 'Something went wrong');
          // callback && callback('error');
        });
    }
  }

  function removeTechnician() {
    updateTechnician(null);
  }

  function highlightCurrentUserAndCurrentTechnician(techniciansList) {
    const values = [{ id: currentUser?.userId, index: 0 }];
    if (currentUser?.userId !== technician?.userId) {
      values.push({ id: technician?.userId, index: 1 });
    }
    const clonedTechniciansList = clone(techniciansList);
    if (clonedTechniciansList?.length) {
      values.forEach((val) => {
        clonedTechniciansList?.forEach((listItem, idx) => {
          if (listItem['userId'] === val.id) {
            clonedTechniciansList.splice(
              val.index,
              0,
              clonedTechniciansList.splice(idx, 1)[0]
            );
          }
        });
      });
    }
    return clonedTechniciansList;
  }

  function technicianListItem({ item }) {
    const clonedValues =
      currentTechnician && currentTechnician.length
        ? clone(currentTechnician)
        : [];

    const alreadyValueSelected = clonedValues.find(
      (value) => value?.userId === item?.userId
    );

    const isCurrentUser = item.userId === currentUser.userId;

    return (
      <TechnicianListItem
        className={`fx actr ${alreadyValueSelected ? 'selected' : ''}`}
      >
        <Avatar initials={item?.name?.[0]} name={item?.name} size="small" />
        <span className="ml-10">
          {isCurrentUser
            ? getTranslatedText(
                intl,
                'ticket.technicians.dropdown.isCurrentUser'
              )
            : item?.name}
        </span>
      </TechnicianListItem>
    );
  }
  if ((!client?.accountId && isMSPProduct) || !site?.id) {
    return <UnknownTicketCard {...props} colors={colors} />;
  }

  return (
    <KanbanCard
      isClassicView={isClassicView}
      hightLightCard={hightLightCard}
      darkTheme={appContext?.darkTheme}
    >
      <Card variant={isHighlight && 'attention'}>
        <Header className="fx fx-sb">
          <IdContainer>
            {isClassicView && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  label=""
                  checked={
                    selectedRecords &&
                    selectedRecords.some((e) => e.ticketId === ticketId)
                  }
                  value={item}
                  onChange={(value, id) => handleCheckboxChange(item)}
                />
              </span>
            )}
            <Tooltip
              title="priority"
              html={
                <TooltipWrap>
                  <div className="type">
                    {getTranslatedText(intl, 'common.type')}:
                    {ticketType ||
                      getTranslatedText(intl, 'app.noData.message')}
                  </div>
                  <div className="priority">
                    {/* <FormattedMessage id="ticket.list.priority.tooltip" /> */}
                    {getTranslatedText(intl, 'ticket.list.priority.tooltip')}
                    {priority || getTranslatedText(intl, 'app.noData.message')
                    //   <FormattedMessage id="app.noData.message" />
                    }
                  </div>
                  <div className="source">{`${getTranslatedText(
                    intl,
                    'ticket.list.source.tooltip'
                  )} ${source ||
                    getTranslatedText(intl, 'app.noData.message')}`}</div>
                </TooltipWrap>
              }
              delay={400}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <StyledTag
                className="priority-cell"
                leftAligned
                maxWidth="140px"
                color="#424242"
                background={colors[priority] || '#9797974D'}
              >
                <SVGIcon
                  // name="inline-error"
                  name={getSourceIcon()}
                  size="xs"
                  color="#424242"
                />
                <span className="ml8">#{displayId}</span>
              </StyledTag>
            </Tooltip>
          </IdContainer>
          {!isStatus && (
            <StyledTag
              className="status-cell uppercase ellipsis"
              color="rgba(0, 0, 0, 0.85)"
              rightAligned
              maxWidth="100px"
              background="#E3EDFF"
            >
              <Tooltip
                title={dompurifier(status)}
                delay={300}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                {status}
              </Tooltip>
            </StyledTag>
          )}
        </Header>
        <div className="mt10 pl10 pr10">
          <ReqClientDetails className="fx actr">
            {source === TICKET_SOURCE.Ai && (
              <AiTag>
                <Tag
                  background="#e8d1ee"
                  padding="1px 3px"
                  color="#ae39b2"
                  className="uppercase mr-5 fw-bold"
                >
                  <FormattedMessage id="ticket.listing.tag.ai" />
                </Tag>
              </AiTag>
            )}
            {source === TICKET_SOURCE.Schedule && (
              <Tooltip
                html={
                  <IconTooltipWrap>
                    {getTranslatedText(
                      intl,
                      'scheduled.ticket.onhover.tooltip'
                    )}
                  </IconTooltipWrap>
                }
                delay={400}
                duration={250}
                position="bottom-end"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <div className="fx actr mr-5">
                  <SVGIcon name="DateTime" size="sm" />
                </div>
              </Tooltip>
            )}
            <Tooltip
              title={dompurifier(
                requester?.name ||
                  `${requester?.firstName || ''} ${requester?.lastName ||
                    'System'}`
              )}
              delay={300}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <ReqClientName maxWidth={isSourceVisible ? '100px' : '110px'}>
                {requester?.name ||
                  `${requester?.firstName || ''} ${requester?.lastName ||
                    'System'}`}
              </ReqClientName>
            </Tooltip>
            <span className="ellipsis">
              {requester?.name || requester?.firstName || requester?.lastName
                ? 'from'
                : 'generated for'}
            </span>
            <Tooltip
              title={dompurifier(
                isMSPProduct ? client?.name : department?.name
              )}
              delay={300}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <ReqClientName maxWidth={isSourceVisible ? '95px' : '110px'}>
                {isMSPProduct ? client?.name || 'NA' : department?.name || 'NA'}
              </ReqClientName>
            </Tooltip>
          </ReqClientDetails>
          <Subject>
            <TicketInfoPopup
              activator={
                <div onClick={openTicket} style={{ lineBreak: 'anywhere' }}>
                  {subject}
                </div>
              }
              data={item}
            />
          </Subject>
          <div className="fx full-width actr jspbtwn mt-10">
            <SlaInfo data={item} />
            {latestReply?.type === 'REQ_REPLY' && (
              <Tooltip
                html={
                  <div>
                    {getTranslatedText(
                      intl,
                      'requester.replied.ticket.onhover.tooltip'
                    )}
                  </div>
                }
                delay={400}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <div className="fx actr ml-10 mr-10">
                  <SVGIcon name="IncomingMail" color="red" size="sm" />
                </div>
              </Tooltip>
            )}
            {!isTechnician && (
              <Tooltip
                title={
                  technician
                    ? technician?.['name'] ||
                      `${technician?.['firstName']} ${technician?.['lastName']}`
                    : getTranslatedText(intl, 'Assignee')
                }
                delay={400}
                duration={250}
                position="top"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <TechnicianContainer className="h20">
                  {technicianLoading ? (
                    <Spinner size="small" />
                  ) : (
                    <>
                      <SearchDropdown
                        currentValues={currentTechnician}
                        updateValues={updateTechnician}
                        listItems={
                          highlightCurrentUserAndCurrentTechnician(
                            technicians?.getTechnicianList?.userList
                          ) || []
                        }
                        RenderItem={technicianListItem}
                        hoverBg={'#e8efff'}
                        activator={
                          <span
                            onClick={() => {
                              getTechniciansList({
                                variables: getTechnicianVariables({
                                  clientIds: [client?.accountId],
                                  siteIds: [site?.id],
                                  currentProduct,
                                  isListInfoRequired: true
                                })
                              });
                            }}
                          >
                            {technician ? (
                              <Avatar
                                initials={
                                  technician?.['name']?.[0] ||
                                  technician?.['firstName']?.[0]
                                }
                                name={
                                  technician?.['name'] ||
                                  technician?.['firstName']
                                }
                                size="extraSmall"
                              />
                            ) : (
                              <span className="assignTech">
                                <SVGIcon
                                  name="Assign"
                                  size="md"
                                  color="#848484"
                                />
                              </span>
                            )}
                          </span>
                        }
                        loading={techLoading}
                        error={techError}
                      />
                      {technician && (
                        <span className="removeTech" onClick={removeTechnician}>
                          <SVGIcon
                            size="sm"
                            name="CircleCancelMinor"
                            color="#979797"
                          />
                        </span>
                      )}
                    </>
                  )}
                </TechnicianContainer>
              </Tooltip>
            )}
          </div>
        </div>
      </Card>
    </KanbanCard>
  );
}

function SlaInfo({ data }) {
  const appContext = useContext(AppContext);
  const { company } = appContext;
  let NoSlaAssociated = () => {
    return (
      <FieldInfoWrapper>
        <div className="flex center no-info">
          <SVGIcon name="HourGlassWirePaused" size="sm" color={'#32323280'} />
          <Value className="no-info">
            <FormattedMessage id="ticket.list.resolutionTimer.noSla" />
          </Value>
        </div>
      </FieldInfoWrapper>
    );
  };
  if (data?.sla) {
    let value = data?.resolutionDueTime;
    let icon = 'hourglasswire';
    let iconColor = '#000000';
    let tooltipValue = '';
    if (value && value > 0) {
      value = convertMinutesToHours(value);
    }

    if (data?.resolutionViolated) {
      iconColor = '#e72f23';
      icon = 'HourglasswireEnd';
      value = 'ticket.list.resolutionTimer.overDue';
      tooltipValue = dayjs(data?.resolutionDueTime).format(
        `${company?.dateFormat || 'MMM DD'}, ${company?.timeFormat ||
          'hh:mm A'}`
      );
    } else if (data.resolutionPauseTime) {
      icon = 'HourGlassWirePaused';
      value = 'ticket.list.resolutionTimer.paused';
    } else if (!data.resolutionViolated && data.resolutionTime) {
      icon = 'HourglasswireEnd';
      value = 'ticket.list.resolutionTimer.achieved';
    }
    if (value)
      return (
        <FieldInfoWrapper>
          <Tooltip
            title={tooltipValue}
            delay={300}
            duration={250}
            position="bottom"
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
          >
            <div className="flex center info">
              <SVGIcon name={icon} size="sm" color={iconColor} />
              <Value color={iconColor}>
                {<FormattedMessage id={value || '--'} />}
              </Value>
            </div>
          </Tooltip>
        </FieldInfoWrapper>
      );
    return <NoSlaAssociated />;
  }
  return <NoSlaAssociated />;
}

export default injectIntl(TicketKanbanCard);

const UnknownTicketCard = (props) => {
  const {
    context,
    item,
    activeTab,
    board,
    dispatch,
    list,
    intl,
    colors,
    updateList,
    selectedRecords = [],
    handleCheckboxChange,
    selectAll,
    isClassicView,
    hightLightCard
  } = props;
  const flags = useFlags();
  const {
    priority,
    displayId,
    subject,
    client,
    requester,
    ticketId,
    status,
    technician,
    source,
    latestReply,
    fromAddress,
    ticketType
  } = item;
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user;
  const isStatus = activeTab === 'status';
  const isTechnician = activeTab === 'technician';
  const isSourceVisible =
    source === TICKET_SOURCE.Schedule || source === TICKET_SOURCE.Ai;

  return (
    <KanbanCard
      bgColor="#FFDFDF"
      isClassicView={isClassicView}
      hightLightCard={hightLightCard}
    >
      <Card>
        <Header className="fx fx-sb">
          <IdContainer>
            {isClassicView && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  label=""
                  checked={
                    selectedRecords &&
                    selectedRecords.some((e) => e.ticketId === ticketId)
                  }
                  value={item}
                  onChange={(value, id) => handleCheckboxChange(item)}
                />
              </span>
            )}
            <Tooltip
              title="priority"
              html={
                <TooltipWrap>
                  <div className="type">
                    {getTranslatedText(intl, 'common.type')}:
                    {ticketType ||
                      getTranslatedText(intl, 'app.noData.message')}
                  </div>
                  <div className="priority">
                    {/* <FormattedMessage id="ticket.list.priority.tooltip" /> */}
                    {getTranslatedText(intl, 'ticket.list.priority.tooltip')}
                    {priority || getTranslatedText(intl, 'app.noData.message')
                    //   <FormattedMessage id="app.noData.message" />
                    }
                  </div>
                </TooltipWrap>
              }
              delay={400}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <StyledTag
                className="priority-cell"
                leftAligned
                maxWidth="140px"
                color="#424242"
                background={colors[priority] || '#9797974D'}
              >
                <SVGIcon name="InlineError" size="xs" color="#424242" />
                <span className="ml8">#{displayId}</span>
              </StyledTag>
            </Tooltip>
          </IdContainer>
          {!isStatus && (
            <StyledTag
              className="status-cell uppercase ellipsis"
              color="rgba(0, 0, 0, 0.85)"
              rightAligned
              maxWidth="140px"
              background="#E3EDFF"
            >
              <Tooltip
                title={dompurifier(status)}
                delay={300}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                {status}
              </Tooltip>
            </StyledTag>
          )}
        </Header>
        <div className="mt10 pl10 pr10">
          <ReqClientDetails>
            <Tooltip
              title={dompurifier(latestReply?.user?.email || 'System')}
              delay={300}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <ReqClientName maxWidth="100%">
                {latestReply?.user?.email || 'System'}
              </ReqClientName>
            </Tooltip>
          </ReqClientDetails>
          <Subject>
            <TicketInfoPopup
              activator={<div style={{ lineBreak: 'anywhere' }}>{subject}</div>}
              data={{ ...item, unknownTicket: true }}
            />
          </Subject>
          <div className="fx full-width actr jend mt-10">
            <Tooltip
              html={
                <div>
                  {getTranslatedText(intl, 'unknown.ticket.onhover.tooltip')}
                </div>
              }
              delay={400}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <div className="fx actr mr-10">
                <SVGIcon name="QuestionMark" color="red" />
              </div>
            </Tooltip>
            <UnknownTicketActions
              ticket={item}
              associationSuccess={updateList}
              deletionSuccess={updateList}
            />
          </div>
        </div>
      </Card>
    </KanbanCard>
  );
};
